import tinycolor from 'tinycolor2';

export {svgReapplyFilter, svgSimpleFilter};
export {svgApplyFilter};

const XMLNS = 'http://www.w3.org/2000/svg';

// saturateRGB is a direct implementation of the matrix operations for
// the saturate filter for SVGs and CSS from
// https://www.w3.org/TR/SVG11/filters.html.
//
// This is preferable to using tinycolor's saturate & desaturate
// because those do a nonlinear conversion to HSL, and perform the
// effect by manipulating the resulting saturation value. This
// operation results in preserved hues & lightness values, but does
// not match SVG & CSS's implementation.
function saturateRGB(color: string, s: number) {
  const {r, g, b} = tinycolor(color).toRgb();

  const saturated = {
    r: (0.213 + (0.787 * s)) * r + (0.715 - (0.715 * s)) * g + (0.072 - (0.072 * s)) * b,
    g: (0.213 - (0.213 * s)) * r + (0.715 + (0.285 * s)) * g + (0.072 - (0.072 * s)) * b,
    b: (0.213 - (0.213 * s)) * r + (0.715 - (0.715 * s)) * g + (0.072 + (0.928 * s)) * b,
  };

  return tinycolor(saturated).toHexString();
}

// The SVG animation and filter effects.
//
// Uses ease-out timing function.
function getSvgFilters(uid: string) {
  return `
  <filter id="grayblur_${uid}" class="diff-backdrop">
    <feColorMatrix id="grayblur-saturate_${uid}" type="saturate" values="0.1"/>
  </filter>
  `;
}

/**
 * Apply the svg filter
 * @param {SVGElement} toNode
 * @param {SVGElement} working
 * @param {string} uid
 */
function applyFilter(toNode: SVGElement, working: SVGElement, uid: string) {
  const layers = working.querySelectorAll('g[data-group="foreground"] > [data-layer-id]');

  // For schematics, use a CSS filter
  if (layers.length <= 0) {
    working.style.filter = `url(#grayblur_${uid})`;
    const docFragment = document.createDocumentFragment();
    const filterGroup = document.createElementNS(XMLNS, 'g');
    // Document fragments don't have innerHTML.
    filterGroup.innerHTML = getSvgFilters(uid);
    for (const childNode of Array.from(filterGroup.childNodes)) {
      docFragment.append(childNode);
    }
    toNode.append(docFragment);
    return;
  }

  for (const layer of layers) {
    const color = layer.getAttribute('color');
    if (color) {
      const desaturatedColor = saturateRGB(color, 0.1);
      layer.setAttribute('data-original-color', color);
      layer.setAttribute('data-desaturated-color', desaturatedColor);
      layer.setAttribute('color', desaturatedColor);
    }
    const fill = layer.getAttribute('fill');
    if (fill) {
      const desaturatedFill = saturateRGB(fill, 0.1);
      layer.setAttribute('data-original-fill', fill);
      layer.setAttribute('data-desaturated-fill', desaturatedFill);
      layer.setAttribute('fill', desaturatedFill);
    }

    // Patterns can also contain color information
    const patternChildren: NodeListOf<SVGElement> = layer.querySelectorAll('pattern > *');
    for (const el of patternChildren) {
      if (el.style.stroke) {
        const desaturatedStroke = saturateRGB(el.style.stroke, 0.1);
        layer.setAttribute('data-original-stroke', el.style.stroke);
        layer.setAttribute('data-desaturated-stroke', desaturatedStroke);
        el.style.stroke = desaturatedStroke;
      }
    }
  }
}

// Add filters to top level svg node
//
// Idempotent
function svgApplyFilter(toNode: SVGElement, uid: string) {
  // If the filter already exists, don't change anything.
  const filter = toNode.querySelector(`#grayblur_${uid}`);
  if (filter !== null) {
    console.warn('SVG filter already found');
    return;
  }

  // Nest working elements inside a working group.
  const group = document.createElementNS(XMLNS, 'g');
  group.classList.add('working');
  const childNodes = Array.from(toNode.querySelectorAll('svg > *:not(defs):not(style)'));
  for (const node of childNodes) {
    group.append(node as Node);
  }
  toNode.append(group);

  applyFilter(toNode, group, uid);
}

function svgSimpleFilter(toNode: SVGElement, uid: string) {
  const working = toNode.querySelector(':scope > g.working') as SVGElement;
  applyFilter(toNode, working, uid);

  for (const node of toNode.querySelectorAll(':scope > .diff > defs')) {
    node.remove();
  }
}

/**
 * Apply the svg filter, ignore preexisting filters
 * (Not idempotent)
 * @param {SVGElement} toNode
 * @param {string} uid
 */

function svgReapplyFilter(toNode: SVGElement, uid: string) {
  // See https://bugzilla.mozilla.org/show_bug.cgi?id=376027
  if (navigator.userAgent.toLowerCase().includes('firefox')) {
    const working = toNode.querySelector('.working');
    if (!working) {
      console.warn('SVG working group not found but filter needs to be reapplied');
      return;
    }
    applyFilter(toNode, working as SVGElement, `${uid}-reapplied`);
  }
}
